import React, { useEffect, useRef, useState } from 'react';
import Yup from '~yup';
import { FormikErrors, useFormik } from 'formik';

import Text from '~components/atoms/Text';
import ActionButton from '~components/atoms/ActionButton';
import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { BranchStatusEnum, FundTypeEnum, GoldTypeEnum, MetalTypeEnum, SilverTypeEnum } from '~utils/enums';

import { TDropdownOption } from '~types/components/labelledDropdown';

import * as FormStyles from './_createInventoryForm.module.scss';
import { GoldSilverInventorySelectedTab } from '~types/redux/state';
import { TCreateInventoryProductParams, TGetSerialNumber } from '~types/feat/gold';
import { IoClose } from 'react-icons/io5';
import * as XLSX from 'xlsx';

type TUploadStatus = {
    fileName?: string;
    status: 'uploading' | 'success' | 'error';
    message?: string;
};

const CreateInventoryForm = () => {
    const dispatch = useAppDispatch();

    const selectedInventoryTab = useAppSelector((state) => state.gold.goldInventory.inventoryListing.selectedTab);

    const isGoldTab = [
        GoldSilverInventorySelectedTab.GoldIslamicPaper,
        GoldSilverInventorySelectedTab.GoldIslamicPhysical,
        GoldSilverInventorySelectedTab.GoldConventionalPaper,
        GoldSilverInventorySelectedTab.GoldConventionalPhysical,
    ].includes(selectedInventoryTab);

    const isSilverTab = [
        GoldSilverInventorySelectedTab.SilverIslamicPaper,
        GoldSilverInventorySelectedTab.SilverIslamicPhysical,
        GoldSilverInventorySelectedTab.SilverConventionalPaper,
        GoldSilverInventorySelectedTab.SilverConventionalPhysical,
    ].includes(selectedInventoryTab);

    const branchesList = useAppSelector((state) => state.branch.allBranches);
    const goldSilverProducts = useAppSelector((state) => state.gold.allProducts);

    const productList = goldSilverProducts;
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const isLoading = useAppSelector((state) => state.gold.loadings.createInventory);

    const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

    const [uploadStatus, setUploadStatus] = useState<Record<number, TUploadStatus>>({});
    const [productSearch, setProductSearch] = useState(
        [
            {
                productName: '',
            },
        ]
    );

    const getInitialMetalType = () => {
        if (isGoldTab) return MetalTypeEnum.Gold;
        return MetalTypeEnum.Silver;
    };

    const validationSchema = Yup.object().shape({
        branchName: Yup.string().
            matchNoLeadingAndTrailingWhiteSpaces()
            .matchMaxOneWhiteSpace()
            .test('check-branch-name',
                'Selected branch does not exist',
                function (value) {
                    if (!value) return false;
                    return branchOptions.some((branch) => branch.label === value);
                }
            )
            .required('Branch name is required'),
        branchCode: Yup.string().required('Branch code is required'),
        products: Yup.array().of(
            Yup.object({
                productId: Yup.string().required('Product ID is required'),
                productName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Product Name is required'),
                amount: Yup.number()
                    .typeError('Quantity must be a number')
                    .test('no-exponential-notation',
                        'Quantity must be a whole number (exponential notation is not allowed)',
                        function (value) {
                            const original = this.originalValue;
                            if (original === undefined || original === null || original === '') return true;
                            return !/e/i.test(String(original));
                        }
                    )
                    .integer('Quantity must be a whole number')
                    .required('Amount is required')
                    .test(
                        'paper-gold-islamic-exact-amount',
                        'Quantity must be exactly 1000 for Paper Gold Islamic (Emas Account-i)',
                        function (value) {
                            const { productId } = this.parent;
                            const originalValue = this.originalValue;

                            const product = productList.find(
                                (p) => p.productId === Number(productId)
                            );

                            const isPaperGoldIslamic =
                                product?.goldType === GoldTypeEnum.PaperGold &&
                                product?.fundType === FundTypeEnum.Islamic;

                            if (!isPaperGoldIslamic) {
                                return true;
                            }

                            return String(originalValue) === '1000';
                        }
                    )
                    .test(
                        'paper-gold-conventional-exact-amount',
                        'Quantity must be exactly 1000 for Paper Gold Conventional (Precious Metal Account)',
                        function (value) {
                            const { productId } = this.parent;
                            const originalValue = this.originalValue;

                            const product = productList.find(
                                (p) => p.productId === Number(productId)
                            );

                            const isPaperGoldConventional =
                                product?.goldType === GoldTypeEnum.PaperGold &&
                                product?.fundType === FundTypeEnum.Conventional;

                            if (!isPaperGoldConventional) {
                                return true;
                            }

                            return String(originalValue) === '1000';
                        }
                    )
                    .min(1, 'Quantity must be more than 0'),
                serialNo: Yup.string()
                    .matchNoLeadingAndTrailingWhiteSpaces()
                    .matchNoLeadingAndTrailingCommas()
                    .matchZeroWhiteSpace()
                    .when('productId', {
                        is: (val: string) => {
                            const product = productList.find(
                                (p) => p.productId === Number(val)
                            );

                            if (!product) return false;

                            if (isGoldTab) {
                                return product.goldType === GoldTypeEnum.PhysicalGold;
                            }

                            if (isSilverTab) {
                                return product.physicalSilverWeight !== null;
                            }

                            return false;
                        },
                        then: (schema) => schema.test('serial-numbers-count',
                            function (value) {
                                const { amount } = this.parent;

                                if (!value) return this.createError({
                                    message: 'Must provide a serial number',
                                });

                                if (amount === 0) return true;
                                const serialNumbers = value.split(',').map(s => s.trim()).filter(s => s.length > 0);

                                if (serialNumbers.length !== amount) {
                                    return this.createError({
                                        message: `Must provide exactly ${amount} serial number${amount !== 1 ? 's' : ''}`,
                                    });
                                }
                                return serialNumbers.length === amount;
                            }
                        ).test('duplicate-serial-numbers',
                            'The provided serial numbers are not unique',
                            function (value) {
                                if (!value) return false;
                                const valuesArray = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
                                return valuesArray.length === new Set(valuesArray).size;
                            }
                        ),
                        otherwise: (schema) => schema.test('serial-numbers-count',
                            'Must provide exactly 1 serial number',
                            function (value) {
                                if (!value) return false;
                                const serialNumbers = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
                                return serialNumbers.length === 1;
                            }
                        ),
                    }),
            }),
        ),
    });

    const CreateInventoryInitialValues = {
        branchName: '',
        branchCode: '',
        products: [{
            productId: 0,
            productName: '',
            amount: '',
            serialNo: '',
        }],
    };

    const formik = useFormik({
        initialValues: CreateInventoryInitialValues,
        validationSchema: validationSchema,
        enableReinitialize: true, // Add this to update validation when serialNumberRequired changes
        onSubmit: values => {
            const newProductValue = values.products.map((product) => {
                const serialNoArray = product.serialNo.split(',');

                return {
                    ...product,
                    productName: undefined,
                    serialNo: serialNoArray,
                    amount: Number(product.amount),
                };
            });

            const payload = {
                ...values,
                products: newProductValue,
                metalType: getInitialMetalType(),
            };

            dispatch(combinedActions.goldSetCreateInventoryParams(payload));
            dispatch(combinedActions.goldCreateInventoryAttempt());
        },
    });

    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active).map((branch) => ({
        label: branch.branchName,
        value: branch.branchCode,
    }));

    const getTabFilteredProducts = () => {
        if (selectedInventoryTab === GoldSilverInventorySelectedTab.GoldConventionalPaper) {
            return productList.filter(product => product.fundType === FundTypeEnum.Conventional && product.goldType === GoldTypeEnum.PaperGold);
        }
        if (selectedInventoryTab === GoldSilverInventorySelectedTab.GoldConventionalPhysical) {
            return productList.filter(product => product.fundType === FundTypeEnum.Conventional && product.goldType === GoldTypeEnum.PhysicalGold);
        }
        if (selectedInventoryTab === GoldSilverInventorySelectedTab.GoldIslamicPaper) {
            return productList.filter(product => product.fundType === FundTypeEnum.Islamic && product.goldType === GoldTypeEnum.PaperGold);
        }
        if (selectedInventoryTab === GoldSilverInventorySelectedTab.GoldIslamicPhysical) {
            return productList.filter(product => product.fundType === FundTypeEnum.Islamic && product.goldType === GoldTypeEnum.PhysicalGold);
        }
        return [];
    };

    // tab-filtered only — does NOT exclude already-selected products, used for validation
    const allTabProductOptions = getTabFilteredProducts().map((product) => ({
        label: product.productName,
        value: product.productId,
    }));

    const filterProductsBySelectedTab = () => {
        let productOptions: TDropdownOption[] = [];

        const isIslamic = [
            GoldSilverInventorySelectedTab.GoldIslamicPaper,
            GoldSilverInventorySelectedTab.GoldIslamicPhysical,
            GoldSilverInventorySelectedTab.SilverIslamicPaper,
            GoldSilverInventorySelectedTab.SilverIslamicPhysical,
        ].includes(selectedInventoryTab);

        const isPhysical = [
            GoldSilverInventorySelectedTab.GoldIslamicPhysical,
            GoldSilverInventorySelectedTab.GoldConventionalPhysical,
            GoldSilverInventorySelectedTab.SilverIslamicPhysical,
            GoldSilverInventorySelectedTab.SilverConventionalPhysical,
        ].includes(selectedInventoryTab);

        const filtered = productList.filter(product => {
            const fundMatch = isIslamic
                ? product.fundType === FundTypeEnum.Islamic
                : product.fundType === FundTypeEnum.Conventional;

            let typeMatch = false;

            if (isGoldTab) {
                typeMatch = isPhysical
                    ? product.goldType === GoldTypeEnum.PhysicalGold
                    : product.goldType === GoldTypeEnum.PaperGold;
            }

            if (isSilverTab) {
                typeMatch = isPhysical
                    ? product.physicalSilverWeight !== null
                    : product.physicalSilverWeight === null;
            }

            const metalMatch = isGoldTab
                ? product.metalType === MetalTypeEnum.Gold
                : product.metalType === MetalTypeEnum.Silver;

            return fundMatch && typeMatch && metalMatch;
        });

        productOptions = filtered.map(product => ({
            label: product.productName,
            value: product.productId,
        }));

        const selectedProducts = formik.values.products;

        if (selectedProducts.length > 0) {
            productOptions = productOptions.filter(
                p => !selectedProducts.some(
                    sp => sp.productId === p.value
                )
            );
        }

        return productOptions;
    };

    const productOptions = filterProductsBySelectedTab();

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            formik.setFieldValue('branchCode', branchCode);
            formik.setFieldValue('branchName', branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '');
        }
    }, [branchesList]);

    useEffect(() => {
        // Note: Not batching this setFieldValue with branchName's onChange setFieldValue because of a race condition
        // the branchName validation will fire first before the update is done, rendering an error for the branchName field
        const newBranch = branchesList.find(branch => branch.branchName === formik.values.branchName);

        if (newBranch) {
            formik.setFieldValue('branchCode', newBranch?.branchCode);
        } else {
            formik.setFieldValue('branchCode', '');
        }
    }, [formik.values.branchName]);

    const handleOnSearchBranch = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('branchName', event.target.value);
    };

    const handleOnSelectBranch = (option: TDropdownOption) => {
        formik.handleChange({ target: { name: 'branchCode', value: option.value } });
    };

    const handleOnSearchProduct = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        // update displayed value only, without updating formik
        const updatedFilters = productSearch.map((item, i) =>
            i === index ? { productName: event.target.value } : item
        );
        setProductSearch(updatedFilters);

        const matchedOption = productOptions.find((option) => option.label === event.target.value);

        if (!matchedOption) {
            formik.setFieldValue(`products.[${index}].productId`, 0);
            formik.setFieldValue(`products.[${index}].productName`, event.target.value);
            return;
        }

        const product = productList.find((productOption) => productOption.productId === matchedOption.value);

        if (!product) {
            formik.setFieldValue(`products.[${index}].productId`, 0);
            formik.setFieldValue(`products.[${index}].productName`, event.target.value);
            return;
        }

        formik.setFieldValue(`products.[${index}].productId`, product.productId);
        formik.setFieldValue(`products.[${index}].productName`, product.productName);
    };

    const handleOnSelectProduct = (option: TDropdownOption, index: number) => {
        const product = productList.find((productOption) => productOption.productId === option.value);

        if (!product) return;
        if (formik.values.products[index].productId === product.productId) return;

        // update displayed value only, without updating formik
        const updatedFilters = productSearch.map((item, i) =>
            i === index ? { productName: product.productName } : item
        );
        setProductSearch(updatedFilters);

        formik.setFieldValue(`products.[${index}].productId`, product.productId);
        formik.setFieldValue(`products.[${index}].productName`, product.productName);
    };

    const handleOnChangeQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange(event);
    };

    const handleAddProductClicked = () =>{
        setProductSearch([...productSearch, { productName: '' }]);

        formik.setFieldValue('products', [
            ...formik.values.products,
            {
                productId: 0,
                productName: '',
                amount: '',
                serialNo: '',
            },
        ]);
    };

    const handleRemoveProductClicked = (index: number) =>{
        if (formik.values.products.length > 1){
            const productSearchArray = productSearch.filter((_, i) => i !== index);
            setProductSearch(productSearchArray);
            formik.values.products.splice(index, 1);
            formik.setFieldValue('products', formik.values.products);
        }

        return;
    };

    const handleSerialExcelUpload = (
        event: React.ChangeEvent<HTMLInputElement>,
        productIndex: number
    ) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // set uploading state
        setUploadStatus(prev => ({
            ...prev,
            [productIndex]: {
                status: 'uploading',
                fileName: file.name,
            },
        }));

        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];

                const rows = XLSX.utils.sheet_to_json<TGetSerialNumber[]>(worksheet, {
                    header: 1,
                    raw: false,
                });

                const serials = rows
                    .slice(1)
                    .flat()
                    .map(cell => String(cell).trim())
                    .filter(cell => cell.length > 0);

                if (serials.length === 0) {
                    throw new Error('No serial numbers found');
                }

                formik.setFieldValue(
                    `products.[${productIndex}].serialNo`,
                    serials.join(',')
                );

                formik.setFieldValue(
                    `products.[${productIndex}].amount`,
                    serials.length
                );

                // success state
                setUploadStatus(prev => ({
                    ...prev,
                    [productIndex]: {
                        status: 'success',
                        fileName: file.name,
                        message: `${serials.length} serial numbers imported`,
                    },
                }));
            } catch (err) {
                setUploadStatus(prev => ({
                    ...prev,
                    [productIndex]: {
                        status: 'error',
                        fileName: file.name,
                        message: 'Failed to read Excel file',
                    },
                }));
            }
        };

        reader.onerror = () => {
            setUploadStatus(prev => ({
                ...prev,
                [productIndex]: {
                    status: 'error',
                    fileName: file.name,
                    message: 'File upload error',
                },
            }));
        };

        reader.readAsArrayBuffer(file);
    };

    const handleClearUploadedFile = (productIndex: number) => {
        if (fileInputRefs.current[productIndex]) {
        fileInputRefs.current[productIndex]!.value = '';
        }

        setUploadStatus(prev => {
            const updated = { ...prev };
            delete updated[productIndex];
            return updated;
        });

        formik.setFieldValue(`products.[${productIndex}].serialNo`, '');
        formik.setFieldValue(`products.[${productIndex}].amount`, '');
    };

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
        >
            <div className={FormStyles['form-container']}>
                <LabelledDropdown
                    inputTestId='create-inventory-branchName-input'
                    optionTestId='create-inventory-branchName-option'
                    id='branchName'
                    name='branchName'
                    label='Branch*: '
                    options={branchOptions}
                    value={formik.values.branchName}
                    error={formik.touched.branchName ? formik.errors.branchName : undefined}
                    onChange={handleOnSearchBranch}
                    onSelectOption={handleOnSelectBranch}
                    // onBlur={formik.handleBlur}
                    noOptionsMessage='No branches found'
                    autoComplete='off'
                    optionSelected={!!branchOptions.find((branch) => branch.label === formik.values.branchName)}
                    disabled={!allBranchAccess}
                />

                <LabelledInput
                    data-testid='create-inventory-branchCode-input'
                    id='branchCode'
                    name='branchCode'
                    label='Branch Code* :'
                    value={formik.values.branchCode}
                    error={formik.touched.branchCode ? formik.errors.branchCode : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <Text
                    isBold
                    label='Products:'
                />
            </div>

            {
                formik.values.products.map( (newProduct, i) => (
                    <div key={i} className={FormStyles['products-container']} >
                        <div className={FormStyles['close-button-container']}>
                            <button
                                className={FormStyles['close-button']}
                                type='button'
                                onClick={() => handleRemoveProductClicked(i)}
                            >
                                <IoClose size={30} />
                            </button>
                        </div>

                        <div className={FormStyles['form-container']}>
                            <LabelledDropdown
                                inputTestId='create-inventory-productName-input'
                                optionTestId='create-inventory-productName-option'
                                id={`products.[${i}].productName`}
                                name={`products.[${i}].productName`}
                                label='Product Name* :'
                                options={productOptions.sort((a, b) => Number(a.value) - Number(b.value))}
                                value={productSearch[i] ? productSearch[i].productName : ''}
                                error={formik.touched.products?.[i]?.productName ? (formik.errors.products?.[i] as FormikErrors<TCreateInventoryProductParams>)?.productName : undefined}
                                onChange={(e) => handleOnSearchProduct(e, i)}
                                onSelectOption={(e) => handleOnSelectProduct(e, i)}
                                // onBlur={formik.handleBlur}
                                noOptionsMessage='No product found'
                                autoComplete='off'
                                optionSelected={!!productOptions.find((product) => product.label === formik.values.products?.[i]?.productName)}
                            />

                            <LabelledInput
                                data-testid='create-inventory-quantity-input'
                                id={`products.[${i}].amount`}
                                name={`products.[${i}].amount`}
                                label='Quantity*: '
                                value={formik.values.products?.[i]?.amount}
                                error={formik.touched.products?.[i]?.amount ? (formik.errors.products?.[i] as FormikErrors<TCreateInventoryProductParams>)?.amount : undefined}
                                onChange={handleOnChangeQuantity}
                                onBlur={formik.handleBlur}
                            />

                            <LabelledInput
                                data-testid='create-inventory-serialNo-input'
                                id={`products.[${i}].serialNo`}
                                name={`products.[${i}].serialNo`}
                                label='Serial Number (comma delimited)* :'
                                value={formik.values.products?.[i]?.serialNo}
                                error={formik.touched.products?.[i]?.serialNo ? (formik.errors.products?.[i] as FormikErrors<TCreateInventoryProductParams>)?.serialNo : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />

                            <div>
                                <Text label='Serial Number File:' isBold className={FormStyles['upload-serial']} />
                                <input
                                    type='file'
                                    accept='.xlsx,.xls,.csv'
                                    style={{ display: 'none' }}
                                    id={`upload-${i}`}
                                    ref={(el) => (fileInputRefs.current[i] = el)}
                                    onChange={(e) => handleSerialExcelUpload(e, i)}
                                />
                                <div className={FormStyles['upload-button']}>
                                    <ActionButton
                                        label='Upload Serial Number Here'
                                        type='button'
                                        onClick={() => document.getElementById(`upload-${i}`)?.click()}
                                    />
                                    {uploadStatus[i]?.fileName && (
                                        <div className={FormStyles['selected-file']}>
                                            <Text
                                                isBold
                                                size='s'
                                                label={`Selected File: ${uploadStatus[i].fileName} ${
                                                    uploadStatus[i].status === 'success'
                                                        ? '(Uploaded successfully)'
                                                        : uploadStatus[i].status === 'error'
                                                            ? `(Error: ${uploadStatus[i].message})`
                                                            : '(Uploading...)'
                                                }`}
                                                className={
                                                    uploadStatus[i].status === 'error'
                                                        ? FormStyles['upload-error']
                                                        : FormStyles['upload-success']
                                                }
                                            />
                                            <IoClose size={18} onClick={() => handleClearUploadedFile(i)} className={FormStyles['clear-file']} />
                                        </div>
                                    )}
                                </div>
                                <Text size='s' label='Supported formats: .XLS, .XLSX, .CSV' />

                            </div>

                        </div>
                    </div>
                ))
            }

            <ActionButton
                testId='create-inventory-add-product-button'
                label='+ Add Product'
                type='button'
                onClick={handleAddProductClicked}
            />

            <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                <div className={FormStyles['button-subcontainer']}>
                    <ActionButton
                        testId='create-inventory-submit-button'
                        label='Create Inventory'
                        type='submit'
                        isLoading={isLoading}
                        disabled={isLoading}
                    />
                </div>
            </div>
        </form>
    );
};

export default CreateInventoryForm;
